import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ErrorComponent from "@/components/ui/error";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  type CompanyAnswer,
  type CompanyAnswerFilters,
  type CompanyAnswerRow,
  listCompanyQuestionAnswers,
  rewriteCompanyQuestionAnswers,
} from "@/hera-api/company-question-answers-api";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useMemo, useState } from "react";

const ACCESSOR_KEYS = [
  "company_name",
  "name",
  "email",
  "phone",
  "address",
  "registrant",
] as const;

type AnswerAccessorKey = (typeof ACCESSOR_KEYS)[number];

const COLUMN_LABELS: Record<AnswerAccessorKey, string> = {
  company_name: "Company name",
  name: "Person name",
  email: "Email",
  phone: "Phone",
  address: "Address",
  registrant: "Registrant",
};

function columnProperties(
  accessorKey: AnswerAccessorKey,
): ColumnProperties<CompanyAnswerRow, AnswerAccessorKey> {
  return {
    displayName: COLUMN_LABELS[accessorKey],
    defaultVisible: true,
    getValue: (row) => row[accessorKey] ?? "",
  };
}

function normalizeAnswerPart(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function answerIdentity(answer: CompanyAnswer): string {
  return [
    answer.company_name,
    answer.name,
    answer.email,
    answer.phone,
    answer.address_line1,
    answer.address_line2,
    answer.city,
    answer.state,
    answer.zip_code,
  ]
    .map(normalizeAnswerPart)
    .join("\u0000");
}

function answersMatch(left: CompanyAnswer, right: CompanyAnswer): boolean {
  return answerIdentity(left) === answerIdentity(right);
}

function emptyAnswer(): CompanyAnswer {
  return {
    name: "",
    company_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
  };
}

interface CompanyQuestionAnswerListProps {
  tabKey: string;
  leadingContent?: ReactNode;
}

export function CompanyQuestionAnswerList({
  tabKey,
  leadingContent,
}: CompanyQuestionAnswerListProps) {
  const queryClient = useQueryClient();
  const queryKey = ["company-question-answers", tabKey] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => listCompanyQuestionAnswers(tabKey),
  });
  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<AnswerAccessorKey, string[]>>
  >({});
  const [visibleRows, setVisibleRows] = useState<CompanyAnswerRow[] | null>(
    null,
  );
  const [selected, setSelected] = useState<CompanyAnswerRow | null>(null);
  const [editing, setEditing] = useState<CompanyAnswerRow | null>(null);
  const [draft, setDraft] = useState<CompanyAnswer>(emptyAnswer());
  const [pending, setPending] = useState<
    | { kind: "merge"; row: CompanyAnswerRow; count: number }
    | {
        kind: "edit";
        row: CompanyAnswerRow;
        replacement: CompanyAnswer;
        count: number;
      }
    | null
  >(null);

  const rows = query.data ?? [];
  const rowsForCount = visibleRows ?? rows;

  const affectedCount = useMemo(() => {
    return (row: CompanyAnswerRow) => {
      const identity = answerIdentity(row.answer);
      return rowsForCount
        .filter((item) => answerIdentity(item.answer) === identity)
        .reduce((sum, item) => sum + item.license_count, 0);
    };
  }, [rowsForCount]);

  const rewrite = useMutation({
    mutationFn: (body: {
      source: CompanyAnswer;
      replacement: CompanyAnswer;
    }) =>
      rewriteCompanyQuestionAnswers(tabKey, {
        source: body.source,
        replacement: body.replacement,
        filters: columnFilters as CompanyAnswerFilters,
      }),
    onSuccess: async (updated, variables) => {
      toast({
        title:
          updated === 1 ? "Updated 1 license" : `Updated ${updated} licenses`,
        variant: "secondary",
      });
      setPending(null);
      setEditing(null);
      const nextRows = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => listCompanyQuestionAnswers(tabKey),
      });
      setSelected((current) => {
        if (!current) return null;
        const stillThere = nextRows.find((row) => row.id === current.id);
        if (stillThere) return stillThere;
        return (
          nextRows.find(
            (row) =>
              row.primary_account_id === current.primary_account_id &&
              answersMatch(row.answer, variables.replacement),
          ) ?? null
        );
      });
    },
    onError: (error) => {
      toast({
        title: "Could not update answers",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  if (query.isPending) {
    return <LoadingScreen message="Loading answers..." />;
  }
  if (query.isError) {
    return (
      <ErrorComponent
        title="Unable to load answers"
        message={getQueryErrorMessage(query.error)}
      />
    );
  }

  const selectedLabel =
    selected?.company_name.trim() ||
    selected?.name.trim() ||
    selected?.registrant ||
    "";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <DashboardTable
        leadingContent={
          <>
            {leadingContent}
            <div className="flex justify-end pb-3">
              <div className="max-w-sm rounded-md border bg-muted/40 px-3 py-2 text-sm">
                {selected ? (
                  <>
                    <div className="text-xs text-muted-foreground">
                      Selected
                    </div>
                    <div className="font-medium">{selectedLabel}</div>
                    <div className="text-muted-foreground">
                      {selected.registrant}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    Select an answer to merge into
                  </span>
                )}
              </div>
            </div>
          </>
        }
        lockToViewport
        data={rows}
        accessorKeys={ACCESSOR_KEYS}
        getColumnProperties={columnProperties}
        getRowId={(row) => row.id}
        tablePersistenceKey={`company-question-answers-${tabKey}`}
        initialSortColumn="registrant"
        initialSortDirection="asc"
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        onVisibleRowsChange={setVisibleRows}
        rowActionsColumnWidth={250}
        renderRowActions={(row) => {
          const isSelected = selected?.id === row.id;
          const sameAnswer = selected
            ? answersMatch(selected.answer, row.answer)
            : false;
          return (
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant={isSelected ? "default" : "outline"}
                onClick={() => setSelected(row)}
              >
                {isSelected ? "Selected" : "Select"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditing(row);
                  setDraft({ ...row.answer });
                }}
              >
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!selected || sameAnswer || rewrite.isPending}
                onClick={() =>
                  setPending({
                    kind: "merge",
                    row,
                    count: affectedCount(row),
                  })
                }
              >
                Merge
              </Button>
            </div>
          );
        }}
      />

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit answer</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ["company_name", "Company name"],
                ["name", "Person name"],
                ["email", "Email"],
                ["phone", "Phone"],
                ["address_line1", "Address line 1"],
                ["address_line2", "Address line 2"],
                ["city", "City"],
                ["state", "State"],
                ["zip_code", "ZIP"],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`company-answer-${key}`}>{label}</Label>
                <Input
                  id={`company-answer-${key}`}
                  value={draft[key]}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!editing}
              onClick={() => {
                if (!editing) return;
                setPending({
                  kind: "edit",
                  row: editing,
                  replacement: draft,
                  count: affectedCount(editing),
                });
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === "merge" ? "Merge answers?" : "Update answers?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will change {pending?.count ?? 0}{" "}
              {(pending?.count ?? 0) === 1 ? "license" : "licenses"} that match
              the current filters. Licenses hidden by the filters are left as
              they are.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={rewrite.isPending || !pending}
              onClick={() => {
                if (!pending) return;
                if (pending.kind === "merge") {
                  if (!selected) return;
                  rewrite.mutate({
                    source: pending.row.answer,
                    replacement: selected.answer,
                  });
                  return;
                }
                rewrite.mutate({
                  source: pending.row.answer,
                  replacement: pending.replacement,
                });
              }}
            >
              {pending?.kind === "merge" ? "Merge" : "Update"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
